import React, {useEffect, useState} from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import {useParams, useSearchParams} from 'react-router-dom';
import {convertToOrderUrl} from 'utils';
import {WsEventType, WsOrderDataType} from 'types';
import {abnkLogo} from 'assets';
import {NotFound} from 'pages';
import {BACKEND_URL} from 'config';
import {Banner, Button, Modal, ModalDescription} from 'components';

type WsParamsType = {
  store_id?: string;
  order_campaign_referred_code?: string;
  checkout_id?: string;
};

interface IPaymentPlanCreateDesktopProps {}

const PaymentPlanCreateDesktop: React.FC<IPaymentPlanCreateDesktopProps> = () => {
  const {country_code: countryCode, store_id, order_campaign_referred_code} = useParams();
  const [search] = useSearchParams();

  const [wsUrl, setWsUrl] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  const storeId = store_id || search.get('store_id');
  const code = order_campaign_referred_code || search.get('order_campaign_referred_code');
  const checkoutId = search.get('checkout_id');
  const hasCancelRedirect = search.get('cancel_redirect') === 'true';
  const canRedirectToMerchant = !!checkoutId && hasCancelRedirect;

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleDiscard = () => {
    if (canRedirectToMerchant) {
      const url = `${BACKEND_URL}/v2/magic/ckt/${checkoutId}/cancelredirect/`;
      window.location.replace(url);
    }
  };

  useEffect(() => {
    let params: WsParamsType = {};
    if (storeId) params = {store_id: storeId};
    if (code) params = {order_campaign_referred_code: code};
    if (checkoutId) params = {checkout_id: checkoutId};
    if (!Object.keys(params).length) return;

    const getWsUrl = async () => {
      const res = await axios.get('/buyer/ws/', {params});
      const data = res?.data?.data;
      if (data?.ws_url) setWsUrl(data.ws_url);
    };
    getWsUrl();
  }, []);

  useEffect(() => {
    if (!wsUrl) return;
    const ws = new WebSocket(wsUrl);
    ws.onmessage = event => {
      const parsedData = JSON.parse(event.data);
      const eventType = parsedData.event_type as WsEventType;
      const data = parsedData[eventType] as WsOrderDataType;
      const {
        order: {store, order_campaign, checkout},
        redirect_url,
      } = data;

      let isCorrectOrder: boolean = false;
      if (store) isCorrectOrder = storeId === store.id;
      if (order_campaign) isCorrectOrder = code === order_campaign.referred_code;
      if (checkout) isCorrectOrder = checkoutId === checkout.id;
      if (redirect_url && isCorrectOrder) window.location.replace(redirect_url);
    };
    return () => {
      ws.close();
    };
  }, [wsUrl]);

  if (!countryCode) return <NotFound />;

  const qrCode = convertToOrderUrl(countryCode, storeId, code, checkoutId);

  if (!qrCode) return <NotFound />;

  return (
    <div>
      {canRedirectToMerchant && (
        <Banner
          text="Back to merchant"
          buttonText="Confirm"
          handlePress={() => setShowModal(true)}
        />
      )}

      <div className="h-screen flex justify-center items-center pt-14 md:pt-0 pb-40 px-3">
        <div className="flex flex-col">
          <img alt="ABNK" className="w-70 self-center mb-5" src={abnkLogo} width={80} />
          <p className="text-center text-xl max-w-2xl mb-10 font-medium">
            Continue with ABNK by scanning the QR code below, and enjoy flexible payments
          </p>
          <div className="flex justify-center">
            <QRCode value={qrCode} />
          </div>
        </div>
      </div>

      {canRedirectToMerchant && (
        <Modal show={showModal} handleClickBackdrop={handleCancel}>
          <ModalDescription text="Discard purchase and go back to merchant's page?" />
          <Button text="Cancel" variant="secondary" onClick={handleCancel} />
          <div className="py-1.5"></div>
          <Button text="Yes, discard" variant="warningPrimary" onClick={handleDiscard} />
        </Modal>
      )}
    </div>
  );
};

export default PaymentPlanCreateDesktop;
